import FrontApplet from '../FrontApplet';
import ITouchEventMessage from '../Touch/ITouchEventMessage';

/**
 * If some transformer returns undefined, transforming message will not be forwarded to certain target
 * window and next transformers will not be called.
 */
export type Transformer<T> = (data: T, target: Window) => T | undefined;

export default class Iframe {
	private frontAppletWindows: Window[];

	/** @internal */
	constructor(
		private window: Window,
		private messagePrefix: string,
		private base: Pick<FrontApplet, 'config' | 'authHash' | 'display' | 'apiJsUri'>,
		private transformers: Transformer<ITouchEventMessage>[],
	) {
		this.frontAppletWindows = [];
	}

	/** @internal */
	public registerFrontAppletLoaderWindow(frontAppletWindow: Window) {
		frontAppletWindow.postMessage(
			{
				type: this.getMessage('api_js_uri'),
				uri: this.base.apiJsUri,
			},
			'*',
		);
	}

	/** @internal */
	public registerBundledFrontAppletWindow(frontAppletWindow: Window) {
		this.frontAppletWindows.push(frontAppletWindow);
		frontAppletWindow.postMessage(
			{
				type: this.getMessage('config.update'),
				config: this.base.config,
			},
			'*',
		);
		frontAppletWindow.postMessage(
			{
				type: this.getMessage('auth_hash.update'),
				authHash: this.base.authHash,
			},
			'*',
		);
		frontAppletWindow.postMessage(
			{
				type: this.getMessage('front_display_version.update'),
				version: this.base.display.version,
			},
			'*',
		);
	}

	/** @internal */
	public forwardToFrontAppletWindows(originalData: any) {
		for (const frontAppletWindow of this.frontAppletWindows) {
			const transformedData = this.transformers.reduce(
				(data: any, transformer: Transformer<ITouchEventMessage>) => (data ? transformer(data, frontAppletWindow) : data),
				originalData,
			);
			if (typeof transformedData === 'undefined') {
				continue;
			}
			frontAppletWindow.postMessage(transformedData, '*');
		}
	}

	/** @internal */
	public forwardFromFrontAppletWindows(frontAppletWindow: Window, data: any) {
		if (this.frontAppletWindows.indexOf(frontAppletWindow) !== -1) {
			this.window.parent.postMessage(data, '*');
		}
	}

	private getMessage(name: string) {
		return this.messagePrefix + '.' + name;
	}
}
